import { FC } from 'react';
import { useAtomValue } from 'jotai';
import { createColumnHelper, ColumnDef, Row } from '@tanstack/react-table';
import { Tag, InlineLoading, Accordion, AccordionItem } from '@carbon/react';
import { PageHeader } from 'components/page-header';
import { StatsBar } from 'components/stats-bar';
import { DataTable } from 'components/data-table';
import {
  duplicateCoverageParametersLoadableAtom,
  duplicateAnalysisParametersLoadableAtom,
} from './atoms';
import type { ParameterGroup, DuplicateParameterRecord, Stats } from './types';
import styles from './page.module.scss';

// --- Shared columns ---

const recordColumnHelper = createColumnHelper<DuplicateParameterRecord>();

const statusColumn = recordColumnHelper.accessor('keep', {
  id: 'keep',
  header: 'Status',
  size: 100,
  cell: (info) => (
    <Tag
      type={info.getValue() ? 'green' : 'red'}
      size="sm"
      className={styles.keepTag}
    >
      {info.getValue() ? 'Keep' : 'Duplicate'}
    </Tag>
  ),
});

const refColumns = [
  recordColumnHelper.accessor('tirRefCount', {
    id: 'tirRefCount',
    header: 'TIR References',
    size: 140,
    meta: { type: 'integer' },
  }),
  recordColumnHelper.accessor('nriRefCount', {
    id: 'nriRefCount',
    header: 'NRI References',
    size: 140,
    meta: { type: 'integer' },
  }),
  recordColumnHelper.accessor('createdAt', {
    id: 'createdAt',
    header: 'Created At',
    size: 200,
    meta: { type: 'datetime' },
  }),
];

const coverageRecordColumns = [
  recordColumnHelper.accessor('id', { id: 'id', header: 'ID', size: 100 }),
  statusColumn,
  recordColumnHelper.accessor('coverageThreshold', {
    id: 'coverageThreshold',
    header: 'Coverage Threshold',
    size: 170,
  }),
  recordColumnHelper.accessor('uatCoverageThreshold', {
    id: 'uatCoverageThreshold',
    header: 'UAT Coverage Threshold',
    size: 190,
  }),
  recordColumnHelper.accessor('minimumDevelopmentAreaCoverage', {
    id: 'minimumDevelopmentAreaCoverage',
    header: 'Min Dev Area Coverage',
    size: 190,
  }),
  ...refColumns,
];

const analysisRecordColumns = [
  recordColumnHelper.accessor('id', {
    id: 'id_analysis',
    header: 'ID',
    size: 100,
  }),
  statusColumn,
  recordColumnHelper.accessor('participationTractFactor', {
    id: 'participationTractFactor',
    header: 'Participation Tract Factor',
    size: 210,
  }),
  ...refColumns,
];

const groupColumnHelper = createColumnHelper<ParameterGroup>();

const groupColumns = [
  groupColumnHelper.accessor((row) => row.subtractName ?? row.subtractId, {
    id: 'subtract',
    header: 'Subtract',
    size: 220,
  }),
  groupColumnHelper.accessor('unitId', {
    id: 'unit',
    header: 'Unit',
    size: 180,
    cell: (info) => {
      const group = info.row.original;
      if (group.unitId === null) return 'Global';
      return group.unitName ?? String(group.unitId);
    },
  }),
  groupColumnHelper.accessor((row) => row.records.length, {
    id: 'recordCount',
    header: 'Record Count',
    size: 140,
    meta: { type: 'integer' },
  }),
  groupColumnHelper.accessor(
    (row) => row.records.reduce((sum, r) => sum + r.tirRefCount, 0),
    {
      id: 'totalTirRefs',
      header: 'Total TIR Refs',
      size: 140,
      meta: { type: 'integer' },
    }
  ),
  groupColumnHelper.accessor(
    (row) => row.records.reduce((sum, r) => sum + r.nriRefCount, 0),
    {
      id: 'totalNriRefs',
      header: 'Total NRI Refs',
      size: 140,
      meta: { type: 'integer' },
    }
  ),
  groupColumnHelper.accessor(
    (row) =>
      [
        row.subtractId,
        row.subtractName,
        row.unitName,
        ...row.records.map((r) => String(r.id)),
      ]
        .filter(Boolean)
        .join(' '),
    {
      id: '_nestedSearch',
      header: '',
      size: 0,
      enableSorting: false,
    }
  ),
];

// --- Section component ---

const DuplicateSection: FC<{
  label: string;
  groups: ParameterGroup[];
  stats: Stats | null;
  isLoading: boolean;
  error: Error | null;
  recordColumns: ColumnDef<DuplicateParameterRecord>[];
  persistenceKey: string;
}> = ({
  label,
  groups,
  stats,
  isLoading,
  error,
  recordColumns,
  persistenceKey,
}) => (
  <AccordionItem
    title={`${label} (${stats?.duplicateGroups ?? 0} groups, ${
      stats?.recordsToRemove ?? 0
    } to remove)`}
    open={false}
  >
    {stats && (
      <StatsBar
        stats={[
          {
            label: 'Total Records',
            value: stats.totalRecords,
            tagType: 'gray',
          },
          {
            label: 'Duplicate Groups',
            value: stats.duplicateGroups,
            tagType: 'red',
          },
          { label: 'To Keep', value: stats.recordsToKeep, tagType: 'green' },
          { label: 'To Remove', value: stats.recordsToRemove, tagType: 'red' },
          {
            label: 'TIR Refs to Reassign',
            value: stats.tirReferencesToReassign,
            tagType: 'purple',
          },
          {
            label: 'NRI Refs to Reassign',
            value: stats.nriReferencesToReassign,
            tagType: 'purple',
          },
        ]}
      />
    )}

    {isLoading && (
      <InlineLoading description={`Loading ${label.toLowerCase()}...`} />
    )}
    {error && <div className={styles.errorMessage}>Error: {error.message}</div>}

    {!isLoading && !error && (
      <div className={styles.tableWrapper}>
        <DataTable
          data={groups}
          columns={groupColumns as ColumnDef<ParameterGroup>[]}
          getRowId={(row) => `${row.subtractId}-${row.unitId ?? 'global'}`}
          persistenceKey={persistenceKey}
          initialTableState={{ columnVisibility: { _nestedSearch: false } }}
          renderRowSubComponent={(row: Row<ParameterGroup>) => (
            <div className={styles.recordsSubTable}>
              <DataTable
                data={row.original.records}
                columns={recordColumns}
                getRowId={(r) => String(r.id)}
                persistenceKey={`${persistenceKey}-${row.original.subtractId}`}
                config={{
                  sorting: false,
                  globalSearch: false,
                  columnFilters: false,
                  columnResizing: false,
                  columnReordering: false,
                  columnPinning: false,
                  columnVisibility: false,
                  fitContent: true,
                }}
              />
            </div>
          )}
          config={{
            sorting: true,
            globalSearch: true,
            columnFilters: true,
            columnResizing: false,
            columnReordering: true,
            columnPinning: false,
            columnVisibility: false,
            fitContent: true,
          }}
        />
      </div>
    )}
  </AccordionItem>
);

// --- Main page ---

function extractLoadableState(loadable: ReturnType<typeof useAtomValue>) {
  const groups =
    (
      loadable as {
        state: string;
        data?: { groups?: ParameterGroup[]; stats?: Stats };
      }
    ).state === 'hasData'
      ? ((loadable as { data: { groups: ParameterGroup[] } }).data?.groups ??
        [])
      : [];
  const stats =
    (loadable as { state: string; data?: { stats?: Stats } }).state ===
    'hasData'
      ? ((loadable as { data: { stats: Stats } }).data?.stats ?? null)
      : null;
  const isLoading = (loadable as { state: string }).state === 'loading';
  const error =
    (loadable as { state: string; error?: unknown }).state === 'hasError'
      ? (loadable as { error: unknown }).error instanceof Error
        ? (loadable as { error: Error }).error
        : new Error('Failed to fetch data')
      : null;
  return { groups, stats, isLoading, error };
}

export const DuplicateParametersPage: FC = () => {
  const coverageLoadable = useAtomValue(
    duplicateCoverageParametersLoadableAtom
  );
  const analysisLoadable = useAtomValue(
    duplicateAnalysisParametersLoadableAtom
  );

  const coverage = extractLoadableState(coverageLoadable);
  const analysis = extractLoadableState(analysisLoadable);

  const totalGroups =
    (coverage.stats?.duplicateGroups ?? 0) +
    (analysis.stats?.duplicateGroups ?? 0);
  const totalToRemove =
    (coverage.stats?.recordsToRemove ?? 0) +
    (analysis.stats?.recordsToRemove ?? 0);

  return (
    <>
      <PageHeader
        breadcrumbs={[]}
        title="Duplicate Parameters"
        actions={null}
      />

      <div className={styles.sectionHeader}>
        <div className={styles.sectionDescription}>
          Subtract intersection parameters with duplicate (subtract_id, unit_id)
          combinations, created by a race condition in concurrent TIA jobs. The
          oldest record in each group is the keeper; the rest are duplicates.
        </div>
        <div className={styles.expectedState}>
          Expected: Empty &mdash; no duplicate parameters should exist
        </div>
      </div>

      <StatsBar
        stats={[
          {
            label: 'Coverage Groups',
            value: coverage.stats?.duplicateGroups ?? 0,
            tagType: totalGroups > 0 ? 'red' : 'green',
          },
          {
            label: 'Analysis Groups',
            value: analysis.stats?.duplicateGroups ?? 0,
            tagType: totalGroups > 0 ? 'red' : 'green',
          },
          {
            label: 'Total to Remove',
            value: totalToRemove,
            tagType: totalToRemove > 0 ? 'red' : 'green',
          },
        ]}
      />

      <Accordion>
        <DuplicateSection
          label="Coverage Parameters"
          groups={coverage.groups}
          stats={coverage.stats}
          isLoading={coverage.isLoading}
          error={coverage.error}
          recordColumns={
            coverageRecordColumns as ColumnDef<DuplicateParameterRecord>[]
          }
          persistenceKey="duplicate-coverage-parameter-groups"
        />
        <DuplicateSection
          label="Analysis Parameters"
          groups={analysis.groups}
          stats={analysis.stats}
          isLoading={analysis.isLoading}
          error={analysis.error}
          recordColumns={
            analysisRecordColumns as ColumnDef<DuplicateParameterRecord>[]
          }
          persistenceKey="duplicate-analysis-parameter-groups"
        />
      </Accordion>
    </>
  );
};
